import { INSTANCE } from './endpoint';
import { ResultResponse } from '../types/generics';

// NOTE: Authorization header is automatically added by request interceptor in endpoint.ts
// No need to manually add it here

// Chatroom Types
export interface IChatroom {
  _id: string;
  room_name: string;
  room_description?: string;
  room_type: 'channel' | 'community';
  linkedEntity: {
    entityType: 'channel' | 'community';
    programId: string;
    communityIdentifier: string;
    channelIdentifier?: string;
  };
  accessControl: {
    type: 'role-based' | 'specific-users';
    roleFilter?: 'all-members' | 'moderators' | 'admins' | 'community-admins';
    allowedUserIds?: string[];
  };
  settings: {
    isDefaultModeratorRoom?: boolean;
    isDefaultCommunityAdminRoom?: boolean;
    allowThreading?: boolean;
    allowFileUploads?: boolean;
    allowLinkUnfurling?: boolean;
  };
  created_by: string;
  _isArchived: boolean;
  createdAt: string;
}

export interface ICreateChannelChatroomPayload {
  programId: string;
  communityIdentifier: string;
  channelIdentifier: string;
  roomName: string;
  accessControl: {
    type: 'role-based' | 'specific-users';
    roleFilter?: 'all-members' | 'moderators' | 'admins' | 'community-admins';
    allowedUserIds?: string[];
  };
  settings?: {
    isDefaultModeratorRoom?: boolean;
    allowThreading?: boolean;
    allowFileUploads?: boolean;
    allowLinkUnfurling?: boolean;
  };
}

export interface ICreateCommunityChatroomPayload {
  programId: string;
  communityIdentifier: string;
  roomName: string;
  accessControl: {
    type: 'role-based' | 'specific-users';
    roleFilter?: 'all-members' | 'moderators' | 'admins' | 'community-admins';
    allowedUserIds?: string[];
  };
  settings?: {
    isDefaultCommunityAdminRoom?: boolean;
    allowThreading?: boolean;
    allowFileUploads?: boolean;
    allowLinkUnfurling?: boolean;
  };
}

// Create channel chatroom
export const createChannelChatroomApi = (
  data: ICreateChannelChatroomPayload
): Promise<ResultResponse<IChatroom>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'POST',
      url: '/v1/chatrooms/channel',
      data,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Create community chatroom
export const createCommunityChatroomApi = (
  data: ICreateCommunityChatroomPayload
): Promise<ResultResponse<IChatroom>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'POST',
      url: '/v1/chatrooms/community',
      data,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Get channel chatrooms
// communityIdentifier is required to filter chatrooms for the specific community
// (avoids returning chatrooms from other communities with channels that have the same name)
export const getChannelChatroomsApi = (
  channelIdentifier: string,
  communityIdentifier?: string
): Promise<ResultResponse<IChatroom[]>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: `/v1/chatrooms/channel/${channelIdentifier}`,
      params: communityIdentifier ? { communityIdentifier } : undefined,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Get community chatrooms
export const getCommunityChatroomsApi = (
  communityIdentifier: string
): Promise<ResultResponse<IChatroom[]>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: `/v1/chatrooms/community/${communityIdentifier}`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Get chatroom by ID
export const getChatroomByIdApi = (
  roomId: string
): Promise<ResultResponse<IChatroom>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: `/v1/chatrooms/${roomId}`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Add members to chatroom
export const addChatroomMembersApi = (
  roomId: string,
  userIds: string[],
  options?: { autoSubscribe?: boolean; enrollmentRole?: string }
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'POST',
      url: `/v1/chatrooms/${roomId}/members`,
      data: { userIds, ...options },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Remove member from chatroom
export const removeChatroomMemberApi = (
  roomId: string,
  userId: string,
  soft: boolean = true
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'DELETE',
      url: `/v1/chatrooms/${roomId}/members/${userId}`,
      params: { soft },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Toggle member access
export const toggleChatroomMemberAccessApi = (
  roomId: string,
  userId: string,
  isActive: boolean
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/${roomId}/members/${userId}/toggle`,
      data: { isActive },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Get chatroom members
export const getChatroomMembersApi = (
  roomId: string,
  includeRemoved: boolean = false
): Promise<ResultResponse<any[]>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: `/v1/chatrooms/${roomId}/members`,
      params: { includeRemoved },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Archive chatroom
export const archiveChatroomApi = (
  roomId: string
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/${roomId}/archive`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Unarchive chatroom
export const unarchiveChatroomApi = (
  roomId: string
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/${roomId}/unarchive`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Update chatroom settings
export const updateChatroomSettingsApi = (
  roomId: string,
  settings: {
    allowThreading?: boolean;
    allowFileUploads?: boolean;
    allowLinkUnfurling?: boolean;
  }
): Promise<ResultResponse<IChatroom>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/${roomId}/settings`,
      data: settings,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Update chatroom (name, description)
export const updateChatroomApi = (
  roomId: string,
  data: {
    roomName?: string;
    roomDescription?: string;
  }
): Promise<ResultResponse<IChatroom>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/${roomId}`,
      data,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Check chatroom access
export const checkChatroomAccessApi = (
  roomId: string
): Promise<ResultResponse<{ hasAccess: boolean }>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: `/v1/chatrooms/${roomId}/access/check`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// ==================== DM-RELATED APIs ====================

// Get all conversations (channel chats, community chats, DMs) for sidebar
export interface IConversationsResponse {
  channelChats: IChatroom[];
  communityChats: IChatroom[];
  directMessages: {
    room: IChatroom;
    unread_count: number;
    last_message_preview?: string;
    last_message_at?: string;
    dm_pinned?: boolean;
    dm_archived?: boolean;
  }[];
}

export const getConversationsApi = (
  communityIdentifier: string,
  channelIdentifier?: string
): Promise<ResultResponse<IConversationsResponse>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: '/v1/chatrooms/conversations',
      params: { communityIdentifier, channelIdentifier },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Create or find DM room
export interface ICreateDMPayload {
  targetId: string;
  programId: string;
  communityIdentifier: string;
  contextChannel?: string;
  isModeratorDM?: boolean;
}

export const createOrFindDMApi = (
  data: ICreateDMPayload
): Promise<ResultResponse<IChatroom>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'POST',
      url: '/v1/chatrooms/direct',
      data,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Get user's DM conversations
export const getDirectMessagesApi = (
  communityIdentifier: string,
  options?: { includeArchived?: boolean; limit?: number; offset?: number }
): Promise<ResultResponse<any[]>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: '/v1/chatrooms/direct',
      params: { communityIdentifier, ...options },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Archive DM conversation
export const archiveDMApi = (
  roomId: string
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/direct/${roomId}/archive`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Unarchive DM conversation
export const unarchiveDMApi = (
  roomId: string
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/direct/${roomId}/unarchive`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Pin DM conversation
export const pinDMApi = (
  roomId: string
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/direct/${roomId}/pin`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Unpin DM conversation
export const unpinDMApi = (
  roomId: string
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/direct/${roomId}/unpin`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Get unread counts for multiple rooms
export const getUnreadCountsApi = (
  roomIds: string[]
): Promise<ResultResponse<Record<string, number>>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: '/v1/chatrooms/unread-counts',
      params: { roomIds: roomIds.join(',') },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Mark room as read
export const markRoomAsReadApi = (
  roomId: string
): Promise<ResultResponse<any>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/chatrooms/${roomId}/read`,
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// Get DM candidates (users available to DM)
export interface IDMCandidate {
  userId: string;
  name: string;
  lastName?: string;
  email: string;
  avatarUrl?: string;
  role: string;
}

export const getDMCandidatesApi = (
  programId: string,
  communityIdentifier: string,
  channelIdentifier?: string,
  search?: string
): Promise<ResultResponse<IDMCandidate[]>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: '/v1/chatrooms/dm-candidates',
      params: { programId, communityIdentifier, channelIdentifier, search },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });
